import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Button } from 'framework/ui/mui-builder';
import type { SyberryTodayWorkItem } from 'domain/people/syberryToday/types';
import AvailabilityActionsSection from 'domain/people/availability/shared/AvailabilityActionsSection';
import SyberryTodayCountBadge from '../SyberryTodayCountBadge/SyberryTodayCountBadge';
import SyberryTodaySpentTimeDrawer from '../SyberryTodaySpentTimeDrawer/SyberryTodaySpentTimeDrawer';
import SyberryTodayTaskCard from '../SyberryTodayTaskCard/SyberryTodayTaskCard';
import type { SyberryTodayDashboardProps } from '../../types';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import {
  ALL_TASKS_BY_DEFAULT_STATUSES,
  ALL_TASKS_ONLY_DASHBOARD_TABS,
  DASHBOARD_TABS,
  SyberryTodayDashboardTab,
} from './constants';
import {
  ACTIONS_SX,
  DASHBOARD_HEADER_SX,
  GROUPED_TASKS_SX,
  TAB_BUTTON_ACTIVE_SX,
  TAB_BUTTON_SX,
  TABS_SX,
  TASK_GROUP_HEADER_SX,
  TASK_GROUP_SECTION_SX,
  TASK_LIST_SX,
} from './styles';
import { getGroupedTasks } from './utils';

const SyberryTodayDashboard = ({
  tasks,
  todayTasks,
  groups,
  planStatus,
  expandedTaskId,
  selectionPendingTaskId,
  isSelectionDisabled,
  onToggleSelection,
  onToggleExpansion,
}: SyberryTodayDashboardProps) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const shouldShowAllTasksOnly = ALL_TASKS_BY_DEFAULT_STATUSES.has(planStatus);
  const [activeTab, setActiveTab] = useState<SyberryTodayDashboardTab>(SyberryTodayDashboardTab.DO_TODAY);
  const effectiveActiveTab = shouldShowAllTasksOnly ? SyberryTodayDashboardTab.ALL : activeTab;
  const dashboardTabs = shouldShowAllTasksOnly ? ALL_TASKS_ONLY_DASHBOARD_TABS : DASHBOARD_TABS;
  const [spentTimeTask, setSpentTimeTask] = useState<Nullable<SyberryTodayWorkItem>>(null);
  const displayedTasks = effectiveActiveTab === SyberryTodayDashboardTab.DO_TODAY ? todayTasks : tasks;
  const groupedTasks = useMemo(
    () => (effectiveActiveTab === SyberryTodayDashboardTab.ALL ? getGroupedTasks(groups, displayedTasks) : []),
    [effectiveActiveTab, groups, displayedTasks],
  );
  const isInterruptionsFeatureEnabled = isFeatureEnabled(Features.INTERRUPTIONS);

  const getTabCount = (tabId: SyberryTodayDashboardTab) =>
    tabId === SyberryTodayDashboardTab.DO_TODAY ? todayTasks.length : tasks.length;

  const renderTaskCard = (task: SyberryTodayWorkItem) => (
    <SyberryTodayTaskCard
      key={task.id}
      task={task}
      isExpanded={expandedTaskId === task.id}
      isSelectionPending={selectionPendingTaskId === task.id}
      isSelectionDisabled={isSelectionDisabled}
      onToggleSelection={onToggleSelection}
      onToggleExpansion={onToggleExpansion}
      onOpenSpentTime={setSpentTimeTask}
    />
  );

  const renderTaskList = () => {
    if (!displayedTasks.length) {
      return (
        <Typography variant="body2" color="textSecondary" mt={2.5}>
          No tasks found
        </Typography>
      );
    }

    if (effectiveActiveTab === SyberryTodayDashboardTab.ALL) {
      return (
        <Box sx={GROUPED_TASKS_SX}>
          {groupedTasks.map(({ group, tasks: groupTasks }) => (
            <Box key={group.id} component="section" aria-label={`${group.name} tasks`} sx={TASK_GROUP_SECTION_SX}>
              <Box sx={TASK_GROUP_HEADER_SX}>
                <Typography variant="h6" color="textPrimary">
                  {group.name}
                </Typography>
                <SyberryTodayCountBadge value={groupTasks.length} />
              </Box>

              <Box sx={TASK_LIST_SX}>{groupTasks.map(renderTaskCard)}</Box>
            </Box>
          ))}
        </Box>
      );
    }

    return <Box sx={TASK_LIST_SX}>{displayedTasks.map(renderTaskCard)}</Box>;
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 72px)',
        padding: '20px 24px 48px',
        backgroundColor: 'background.paper',
        color: 'text.secondary',
        '@media (max-width: 900px)': {
          padding: '18px 14px 32px',
        },
      }}
    >
      <Box sx={DASHBOARD_HEADER_SX}>
        <Box aria-label="Syberry Today task tabs" role="tablist" sx={TABS_SX}>
          {dashboardTabs.map(tab => (
            <Button
              key={tab.id}
              aria-selected={effectiveActiveTab === tab.id}
              disableFocusRipple
              disableRipple
              role="tab"
              sx={[TAB_BUTTON_SX, effectiveActiveTab === tab.id && TAB_BUTTON_ACTIVE_SX]}
              tabIndex={effectiveActiveTab === tab.id ? 0 : -1}
              type="button"
              variant="text"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <Box component="span" sx={{ marginTop: 0.25 }}>
                <SyberryTodayCountBadge value={getTabCount(tab.id)} />
              </Box>
            </Button>
          ))}
        </Box>

        {isInterruptionsFeatureEnabled ? (
          <Box sx={ACTIONS_SX}>
            <AvailabilityActionsSection />
          </Box>
        ) : null}
      </Box>

      {renderTaskList()}

      <SyberryTodaySpentTimeDrawer task={spentTimeTask} onClose={() => setSpentTimeTask(null)} />
    </Box>
  );
};

export default SyberryTodayDashboard;
